import * as constants from '../constants/constants';

export interface NotificationTriggeredByUser {
    id: number;
    firstname: string;
    lastname: string;
    avatar: string | null;
}

export interface NotificationEventContext {
    source_type: string | null;
    source_id: number | null;
    // Free-form structured payload — shape varies per event type. Drawer cards
    // pick the fields they care about (e.g. project.created reads client_name +
    // client_id; production.process.returned reads reason + responsibility_id).
    // Loose typing on purpose: each card validates its own expectations.
    payload?: Record<string, any> | null;
    event_type?: {
        key: string;
    } | null;
    // Eloquent serializes the `triggeredBy()` belongsTo relation as
    // `triggered_by` (snake_case of the method name). The broadcast event
    // mirrors the same key so polling and live arrivals expose one shape.
    triggered_by?: NotificationTriggeredByUser | null;
}

export interface NotificationItem {
    id: number;
    rendered_title: string;
    rendered_body: string;
    rendered_action_url: string | null;
    rendered_image_url: string | null;
    priority: string;
    category: string | null;
    locale: string;
    read_at: string | null;
    seen_at: string | null;
    starred: boolean;
    pinned: boolean;
    muted_until: string | null;
    expires_at: string | null;
    created_at: string;
    correlation_id: string;
    // Eager-loaded from notification_events. Used by:
    //   - "Mute source" row action (source_type + source_id)
    //   - Drawer cards (event_type.key picks the layout; payload supplies
    //     the structured fields; triggered_by_user shows "Created by X")
    event?: NotificationEventContext | null;
}

export interface NotificationsState {
    items: NotificationItem[];
    unreadCount: number;
    loading: boolean;
    // Items that arrived since the last manual load — held in a side slot so
    // the user's current scroll position + active selection don't jump when
    // new things stream in. Merged into `items` only when the user clicks
    // the "N new" pill.
    pendingNew: NotificationItem[];
    // Notifications whose source is actively muted — populated by the Muted
    // tab on the archive page (separate API call with muted_only=1). Kept
    // separate from `items` because the default inbox excludes muted ones;
    // mixing them would re-pollute the inbox derivation logic.
    mutedItems: NotificationItem[];
    mutedLoading: boolean;
}

const initialState: NotificationsState = {
    items: [],
    unreadCount: 0,
    loading: false,
    pendingNew: [],
    mutedItems: [],
    mutedLoading: false,
};

const notifications = (state: NotificationsState = initialState, action: any): NotificationsState => {
    switch (action.type) {
        case constants.NOTIFICATIONS_SET_ITEMS: {
            return {
                ...state,
                items: Array.isArray(action.payload?.items) ? action.payload.items : [],
                unreadCount: typeof action.payload?.unreadCount === 'number'
                    ? action.payload.unreadCount
                    : state.unreadCount,
            };
        }
        case constants.NOTIFICATIONS_SET_UNREAD_COUNT: {
            return {
                ...state,
                unreadCount: typeof action.payload === 'number' ? action.payload : 0,
            };
        }
        case constants.NOTIFICATIONS_MARK_READ: {
            const id = action.payload;
            const now = new Date().toISOString();
            const items = state.items.map((item) =>
                item.id === id && !item.read_at ? { ...item, read_at: now } : item,
            );
            // Decrement unread count only if we actually flipped an unread item.
            const flipped = state.items.some((item) => item.id === id && !item.read_at);
            return {
                ...state,
                items,
                unreadCount: Math.max(0, state.unreadCount - (flipped ? 1 : 0)),
            };
        }
        case constants.NOTIFICATIONS_MARK_ALL_READ: {
            const now = new Date().toISOString();
            return {
                ...state,
                items: state.items.map((item) => (item.read_at ? item : { ...item, read_at: now })),
                unreadCount: 0,
            };
        }
        case constants.NOTIFICATIONS_SET_LOADING: {
            return { ...state, loading: !!action.payload };
        }
        case constants.NOTIFICATIONS_TOGGLE_STAR: {
            const id = action.payload;
            return {
                ...state,
                items: state.items.map((item) =>
                    item.id === id ? { ...item, starred: !item.starred } : item,
                ),
            };
        }
        case constants.NOTIFICATIONS_SET_SNOOZE: {
            const { id, until } = action.payload || {};
            return {
                ...state,
                items: state.items.map((item) =>
                    item.id === id ? { ...item, muted_until: until || null } : item,
                ),
            };
        }
        case constants.NOTIFICATIONS_SET_PENDING_NEW: {
            // Replace pendingNew with the incoming set, deduped against items
            // already in the visible list (avoids double-counting if a poll
            // races a manual fetch).
            const incoming: NotificationItem[] = Array.isArray(action.payload) ? action.payload : [];
            const existingIds = new Set(state.items.map((i) => i.id));
            const deduped = incoming.filter((i) => !existingIds.has(i.id));
            return { ...state, pendingNew: deduped };
        }
        case constants.NOTIFICATIONS_ADD_PENDING_NEW: {
            // Append a single incoming item from the WS stream. De-dupe
            // against both the visible list AND the existing pendingNew so
            // a server retransmit doesn't double the pill count.
            const incoming = action.payload as NotificationItem;
            if (!incoming || typeof incoming.id !== 'number') return state;
            const existingIds = new Set<number>([
                ...state.items.map((i) => i.id),
                ...state.pendingNew.map((i) => i.id),
            ]);
            if (existingIds.has(incoming.id)) return state;
            return { ...state, pendingNew: [incoming, ...state.pendingNew] };
        }
        case constants.NOTIFICATIONS_BUMP_UNREAD: {
            // Increment the bell badge when a WS event arrives — saves us a
            // round-trip to /notifications/unread-count just to learn what
            // we already know from the broadcast payload.
            return { ...state, unreadCount: state.unreadCount + 1 };
        }
        case constants.NOTIFICATIONS_MERGE_PENDING: {
            if (state.pendingNew.length === 0) return state;
            // Pending items come first (newest), then the existing list.
            // De-dupe by id in case the same item is somehow present in both.
            const seen = new Set<number>();
            const merged: NotificationItem[] = [];
            for (const item of [...state.pendingNew, ...state.items]) {
                if (seen.has(item.id)) continue;
                seen.add(item.id);
                merged.push(item);
            }
            return { ...state, items: merged, pendingNew: [] };
        }
        case constants.NOTIFICATIONS_SET_MUTED_ITEMS: {
            const incoming: NotificationItem[] = Array.isArray(action.payload) ? action.payload : [];
            return { ...state, mutedItems: incoming };
        }
        case constants.NOTIFICATIONS_SET_MUTED_LOADING: {
            return { ...state, mutedLoading: !!action.payload };
        }
        case constants.NOTIFICATIONS_REMOVE_BY_SOURCE: {
            // After an unmute action, the matching items naturally flow BACK
            // into the inbox on next refetch. This action sweeps them OUT of
            // mutedItems so the Muted tab doesn't show stale entries between
            // the click and the refetch. Payload: { entity_type, entity_id }.
            const { entity_type, entity_id } = action.payload || {};
            if (!entity_type || entity_id == null) return state;
            return {
                ...state,
                mutedItems: state.mutedItems.filter((i) => {
                    const t = i.event?.source_type;
                    const id = i.event?.source_id;
                    return !(t === entity_type && id === entity_id);
                }),
            };
        }
        default:
            return state;
    }
};

export default notifications;
